import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import isEqual from 'lodash/fp/isEqual';
import isUndefined from 'lodash/fp/isUndefined';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Unit } from '../../common/constants';
import { LabeledNumberInput } from '../../components/LabeledNumberInput/LabeledNumberInput';
import { LabeledSelect } from '../../components/LabeledSelect/LabeledSelect';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { useGetVehicleProfileDetailsQuery } from '../../store/facade/facadeApiV3';
import {
    selectBatteryCapacity,
    selectBatteryPacks,
    selectIsEV,
    selectIsProfileSubmited,
    selectProfileType,
} from '../../store/vehicleProfile/vehicleProfileSelectors';
import { changeBatteryCapacity, changeBatteryPacks } from '../../store/vehicleProfile/vehicleProfileSlice';
import {
    HEREVehicleAttibuteNames,
    VEHCILE_ATTRIBUTES_UNITS,
    VehicleType,
} from '../../store/vehicleProfile/vehicleProfileTypes';

export const VehicleBatterySelect = () => {
    const [options, setOptions] = useState<SelectOption[]>();

    const dispatch = useAppDispatch();

    const isEV = useAppSelector(selectIsEV).value;
    const isProfileSubmited = useAppSelector(selectIsProfileSubmited);
    const batteryCapacity = useAppSelector(selectBatteryCapacity);
    const batteryPacks = useAppSelector(selectBatteryPacks);
    const selectedProfile = useAppSelector(selectProfileType);
    const { data: profileDetails } = useGetVehicleProfileDetailsQuery(
        { id: selectedProfile as string },
        { skip: isUndefined(selectedProfile) || !isEV || selectedProfile === VehicleType.CUSTOM_EV }
    );

    const intl = useIntl();
    const batteryLabel = intl.formatMessage({ id: 'intl-msg:smartRoute.vehicle.batteryPack' });

    const isCustomProfile = selectedProfile === VehicleType.CUSTOM_EV || selectedProfile === VehicleType.CUSTOM_ICE;

    useEffect(() => {
        const capacityPerBattery = profileDetails?.batteryCapacityPerPack;
        if (profileDetails && capacityPerBattery && profileDetails.emptyWeightVehicle) {
            const packOptions = profileDetails.emptyWeightVehicle.map(it => {
                return {
                    id: it.packs.toString(),
                    label: `${capacityPerBattery * it.packs} ${Unit.KWH} - ${it.packs} ${batteryLabel}`,
                };
            });
            setOptions(packOptions);
            const profileBatteryPacks = profileDetails?.defaultInstalledBatteryPackCount;

            if (!batteryCapacity.isEditable) {
                // Battery is locked by asset data — don't override packs selection
                return;
            }

            if (profileBatteryPacks && !isEqual(0)(profileBatteryPacks)) {
                handleBatteryPacksChange(profileBatteryPacks.toString());
            }

            if (!isProfileSubmited) {
                dispatch(
                    changeBatteryPacks({
                        profileDetails,
                        batteryPackCount: batteryPacks ?? profileBatteryPacks,
                    })
                );
            }
        }
    }, [profileDetails]);

    const handleBatteryCapacityChange = (selectedBatteryCapacity: number | undefined) => {
        if (isUndefined(selectedBatteryCapacity)) {
            return;
        }

        dispatch(changeBatteryCapacity({ value: selectedBatteryCapacity, isEditable: true }));
    };

    const handleBatteryPacksChange = (selectedOption: string) => {
        const batteryPackCount = Number(selectedOption);
        if (isUndefined(batteryPackCount)) {
            return;
        }
        dispatch(
            changeBatteryPacks({
                profileDetails,
                batteryPackCount,
            })
        );
    };

    if (isCustomProfile) {
        return (
            <LabeledNumberInput
                integerOnly
                id='vehicle-battery-type'
                className='flex-1-1-0'
                labelId='intl-msg:smartRoute.vehicle.batteryCapacity'
                value={batteryCapacity.value}
                unit={VEHCILE_ATTRIBUTES_UNITS[HEREVehicleAttibuteNames.CHARGING_POWER]}
                onChange={handleBatteryCapacityChange}
            />
        );
    }

    return (
        <LabeledSelect
            className='flex-1-1-0'
            label={<FormattedMessage id='intl-msg:smartRoute.vehicle.batteryCapacity' />}
            optionLabels={options ?? []}
            selectedOption={batteryPacks?.toString()}
            onChange={handleBatteryPacksChange}
            disabled={!batteryCapacity.isEditable}
        />
    );
};
